import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingTasks, confirmTask } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CheckCircle2,
  ClipboardList,
  Calendar,
  User,
  Loader2,
  MapPin,
} from 'lucide-react';

const TaskConfirmationPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const data = await getPendingTasks();
      setTasks(data);
    } catch (error) {
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedTask) return;

    setIsConfirming(true);
    try {
      await confirmTask(selectedTask.id);
      toast.success(t('taskConfirmed'));
      setSelectedTask(null);
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setIsConfirming(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-amber-100 text-amber-800',
      urgent: 'bg-red-100 text-red-800',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="task-confirmation-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold font-heading text-foreground">{t('taskConfirmation')}</h1>
        <p className="text-muted-foreground mt-2">{t('tasksNeedingConfirmation')}</p>
      </div>

      {/* Tasks Grid */}
      {tasks.length > 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {tasks.map((task) => (
            <Card
              key={task.id}
              className="work-order-card cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedTask(task)}
              data-testid={`task-card-${task.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {t('task')}
                      </Badge>
                      <Badge className={getPriorityColor(task.priority)}>
                        {task.priority}
                      </Badge>
                      <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                        {task.farm}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold font-heading mb-2">{task.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {task.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        {task.assigned_staff_name || 'Unassigned'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {task.section_code}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Created: {format(new Date(task.created_at), 'MMM d, yyyy')}
                      </span>
                      {task.estimated_cost > 0 && (
                        <span className="font-semibold">
                          Material Cost: {task.currency} {task.estimated_cost?.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button size="sm" data-testid={`confirm-button-${task.id}`}>
                    {t('confirmTask')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12">
            <div className="text-center text-muted-foreground">
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4 opacity-50 text-green-500" />
              <p className="text-lg mb-2">{t('allCaughtUp')}</p>
              <p className="text-sm">{t('noTasksPending')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{t('confirmTask')}</DialogTitle>
            <DialogDescription>
              Review task details and confirm completion
            </DialogDescription>
          </DialogHeader>

          {selectedTask && (
            <div className="space-y-6">
              {/* Task Details */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold font-heading mb-2">{selectedTask.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedTask.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                    <p className="text-sm font-medium">
                      {selectedTask.assigned_staff_name || 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Section</p>
                    <p className="text-sm font-medium">{selectedTask.section_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Farm</p>
                    <p className="text-sm font-medium">
                      {selectedTask.farm === 'LP' ? 'La Punta' : 'Rancagua'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Priority</p>
                    <p className="text-sm font-medium capitalize">{selectedTask.priority}</p>
                  </div>
                  {selectedTask.deadline && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Deadline</p>
                      <p className="text-sm font-medium">
                        {format(new Date(selectedTask.deadline), 'MMM d, yyyy')}
                      </p>
                    </div>
                  )}
                  {selectedTask.estimated_cost > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Material Cost</p>
                      <p className="text-sm font-medium">
                        {selectedTask.currency} {selectedTask.estimated_cost?.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Materials if any */}
                {selectedTask.materials_farm_provides && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Farm Materials</p>
                    <p className="text-sm">{selectedTask.materials_farm_provides}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSelectedTask(null)}
              data-testid="cancel-confirm-button"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-task-button"
            >
              {isConfirming ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              {t('confirmTask')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskConfirmationPage;
